import { z } from 'zod'
import type { BillingState, LockReason } from '../enums/billing-state.enum.js'
import { instantSchema } from '../validators/temporal.validator.js'

/**
 * Where an organization stands on billing, as its people and the super admin see
 * it. Each state carries only the facts that exist in that state: a lock reason
 * only while locked, the grace end while a payment is failing (and on the lock
 * that follows one), and a deletion date only after a canceled trial.
 *
 * The counts and the dates Stripe reported are on every state — a trial end can
 * still be stored after the trial, and the bill is the venue count either way.
 *
 * Two `locked` variants, split on the reason, because a grace end exists only
 * for non-payment. Zod's discriminated union wants one schema per `state`
 * value, so this is a `z.union`; the inferred type still narrows on `state`.
 */

const summaryFacts = {
  /** When the trial ends, extensions included. */
  trialEndsAt: instantSchema.nullable(),
  /** When an ending comp stops giving full access. */
  compEndsAt: instantSchema.nullable(),
  /** The end of the period already paid for. */
  currentPeriodEndsAt: instantSchema.nullable(),
  /** Days already added to the current trial / grace period, against the 14-day cap. */
  trialExtendedDays: z.number().int().nonnegative(),
  graceExtendedDays: z.number().int().nonnegative(),
  /** Venues not archived — what the bill counts. */
  activeVenueCount: z.number().int().nonnegative(),
  /** What this company contributes to monthly recurring revenue, in cents. */
  monthlyCents: z.number().int().nonnegative(),
}

/** Lock reasons other than non-payment. The spec checks this list is exactly that. */
const OTHER_LOCK_REASONS = ['cancelled', 'comp_ended'] as const satisfies readonly Exclude<LockReason, 'non_payment'>[]

const plain = <S extends BillingState>(state: S) => z.object({ state: z.literal(state), ...summaryFacts })

export const billingSummaryDtoSchema = z.union([
  plain('signup_incomplete'),
  plain('trialing'),
  plain('active'),
  plain('cancelling'),
  z.object({ state: z.literal('past_due'), graceEndsAt: instantSchema, ...summaryFacts }),
  z.object({
    state: z.literal('locked'),
    lockReason: z.literal('non_payment'),
    graceEndsAt: instantSchema,
    ...summaryFacts,
  }),
  z.object({
    state: z.literal('locked'),
    lockReason: z.enum(OTHER_LOCK_REASONS),
    ...summaryFacts,
  }),
  z.object({ state: z.literal('trial_cancelled'), deletesAt: instantSchema, ...summaryFacts }),
  plain('comped'),
  plain('comp_ending'),
])

export type BillingSummaryDto = z.infer<typeof billingSummaryDtoSchema>
